"use client";

import type {
    CSSProperties,
    ComponentPropsWithRef,
    HTMLAttributes,
    KeyboardEvent,
    ReactNode,
    Ref,
} from "react";
import {
    cloneElement,
    createContext,
    isValidElement,
    useCallback,
    useContext,
    useEffect,
    useState,
} from "react";
import useEmblaCarousel, { type UseEmblaCarouselType } from "embla-carousel-react";
import { cx } from "@/utils/cx";

type CarouselApi = UseEmblaCarouselType[1];
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>;
type CarouselOptions = UseCarouselParameters[0];
type CarouselPlugin = UseCarouselParameters[1];

type CarouselProps = {
    opts?: CarouselOptions;
    plugins?: CarouselPlugin;
    orientation?: "horizontal" | "vertical";
    setApi?: (api: CarouselApi) => void;
};

type CarouselContextProps = CarouselProps & {
    carouselRef: UseEmblaCarouselType[0];
    api: CarouselApi;
    scrollPrev: () => void;
    scrollNext: () => void;
    canScrollPrev: boolean;
    canScrollNext: boolean;
    selectedIndex: number;
    scrollSnaps: number[];
};

const CarouselContext = createContext<CarouselContextProps | null>(null);

export const useCarousel = () => {
    const context = useContext(CarouselContext);
    if (!context) {
        throw new Error("The `useCarousel` hook must be used within a <Carousel.Root>");
    }
    return context;
};

const CarouselRoot = ({
    orientation = "horizontal",
    opts,
    setApi,
    plugins,
    className,
    children,
    ...props
}: ComponentPropsWithRef<"div"> & CarouselProps) => {
    const [carouselRef, api] = useEmblaCarousel(
        {
            ...opts,
            axis: orientation === "horizontal" ? "x" : "y",
        },
        plugins
    );
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

    const onInit = useCallback((emblaApi: CarouselApi) => {
        if (!emblaApi) return;
        setScrollSnaps(emblaApi.scrollSnapList());
    }, []);

    const onSelect = useCallback((emblaApi: CarouselApi) => {
        if (!emblaApi) return;
        setCanScrollPrev(emblaApi.canScrollPrev());
        setCanScrollNext(emblaApi.canScrollNext());
        setSelectedIndex(emblaApi.selectedScrollSnap());
    }, []);

    const scrollPrev = useCallback(() => {
        api?.scrollPrev();
    }, [api]);

    const scrollNext = useCallback(() => {
        api?.scrollNext();
    }, [api]);

    const handleKeyDown = useCallback(
        (event: KeyboardEvent<HTMLDivElement>) => {
            if (event.key === "ArrowLeft") {
                event.preventDefault();
                scrollPrev();
            } else if (event.key === "ArrowRight") {
                event.preventDefault();
                scrollNext();
            }
        },
        [scrollPrev, scrollNext]
    );

    useEffect(() => {
        if (!api || !setApi) return;
        setApi(api);
    }, [api, setApi]);

    useEffect(() => {
        if (!api) return;
        onInit(api);
        onSelect(api);
        api.on("reInit", onInit);
        api.on("reInit", onSelect);
        api.on("select", onSelect);
        return () => {
            api.off("select", onSelect);
        };
    }, [api, onInit, onSelect]);

    const value: CarouselContextProps = {
        carouselRef,
        api,
        scrollPrev,
        scrollNext,
        canScrollPrev,
        canScrollNext,
        selectedIndex,
        scrollSnaps,
        orientation,
        opts,
        plugins,
        setApi,
    };

    return (
        <CarouselContext.Provider value={value}>
            <div
                className={cx(className)}
                onKeyDown={handleKeyDown}
                ref={props.ref}
                {...props}
            >
                {children}
            </div>
        </CarouselContext.Provider>
    );
};

interface CarouselContentProps extends ComponentPropsWithRef<"div"> {
    className?: string;
    overflowHidden?: boolean;
}

const CarouselContent = ({
    className,
    overflowHidden = true,
    children,
    ...props
}: CarouselContentProps) => {
    const { carouselRef, orientation } = useCarousel();
    return (
        <div
            className={cx(
                "w-full min-w-0 max-w-full overflow-x-hidden overflow-y-hidden",
                overflowHidden && "overflow-hidden",
                className
            )}
            ref={carouselRef}
            {...props}
        >
            <div
                className={cx(
                    "flex flex-nowrap touch-pan-y",
                    orientation === "vertical" && "flex-col"
                )}
                style={{ touchAction: orientation === "horizontal" ? "pan-x pinch-zoom" : undefined }}
            >
                {children}
            </div>
        </div>
    );
};

const CarouselItem = ({ className, style, ...props }: ComponentPropsWithRef<"div">) => {
    const hasFixedSize = style?.width != null || style?.flexBasis != null;
    return (
        <div
            className={cx(
                "min-w-0 shrink-0",
                hasFixedSize ? "" : "basis-full",
                className
            )}
            style={style}
            {...props}
        />
    );
};

interface TriggerRenderProps {
    isDisabled: boolean;
    onClick: () => void;
}

interface TriggerProps {
    ref?: Ref<HTMLButtonElement>;
    asChild?: boolean;
    direction: "prev" | "next";
    children: ReactNode | ((props: TriggerRenderProps) => ReactNode);
    style?: CSSProperties;
    className?: string | ((args: { isDisabled: boolean }) => string);
}

const Trigger = ({
    className,
    children,
    asChild,
    direction,
    style,
}: TriggerProps) => {
    const { scrollPrev, canScrollNext, scrollNext, canScrollPrev } = useCarousel();
    const isDisabled = direction === "prev" ? !canScrollPrev : !canScrollNext;
    const handleClick = () => {
        if (isDisabled) return;
        direction === "prev" ? scrollPrev() : scrollNext();
    };
    const computedClassName =
        typeof className === "function" ? className({ isDisabled }) : className;
    const defaultAriaLabel = direction === "prev" ? "Previous slide" : "Next slide";

    if (typeof children === "function") {
        return <>{children({ isDisabled, onClick: handleClick })}</>;
    }
    if (asChild && isValidElement(children)) {
        return cloneElement(children, {
            onClick: handleClick,
            disabled: isDisabled,
            "aria-label": defaultAriaLabel,
            style: { ...(children.props as HTMLAttributes<HTMLElement>).style, ...style },
            className: [computedClassName, (children.props as HTMLAttributes<HTMLElement>).className]
                .filter(Boolean)
                .join(" ") || undefined,
        } as HTMLAttributes<HTMLElement>);
    }
    return (
        <button
            type="button"
            aria-label={defaultAriaLabel}
            disabled={isDisabled}
            onClick={handleClick}
            className={computedClassName}
            style={style}
        >
            {children}
        </button>
    );
};

const CarouselPrevTrigger = (props: Omit<TriggerProps, "direction">) => (
    <Trigger direction="prev" {...props} />
);
const CarouselNextTrigger = (props: Omit<TriggerProps, "direction">) => (
    <Trigger direction="next" {...props} />
);

interface CarouselIndicatorRenderProps {
    isSelected: boolean;
    onClick: () => void;
}

interface CarouselIndicatorProps {
    index: number;
    asChild?: boolean;
    isSelected?: boolean;
    children?: ReactNode | ((props: CarouselIndicatorRenderProps) => ReactNode);
    style?: CSSProperties;
    className?: string | ((args: { isSelected: boolean }) => string);
}

const CarouselIndicator = ({
    index,
    isSelected: isSelectedProp = false,
    children,
    asChild,
    className,
    style,
}: CarouselIndicatorProps) => {
    const { api, selectedIndex } = useCarousel();
    const isSelected = isSelectedProp || selectedIndex === index;
    const handleClick = () => {
        api?.scrollTo(index);
    };
    const computedClassName =
        typeof className === "function" ? className({ isSelected }) : className;
    const defaultAriaLabel = `Go to slide ${index + 1}`;

    if (typeof children === "function") {
        return <>{children({ isSelected, onClick: handleClick })}</>;
    }
    if (asChild && isValidElement(children)) {
        return cloneElement(children, {
            onClick: handleClick,
            "aria-label": defaultAriaLabel,
            "aria-current": isSelected ? "true" : undefined,
            style: { ...(children.props as HTMLAttributes<HTMLElement>).style, ...style },
            className: [computedClassName, (children.props as HTMLAttributes<HTMLElement>).className]
                .filter(Boolean)
                .join(" ") || undefined,
        } as HTMLAttributes<HTMLElement>);
    }
    return (
        <button
            type="button"
            aria-label={defaultAriaLabel}
            aria-current={isSelected ? "true" : undefined}
            onClick={handleClick}
            className={computedClassName}
            style={style}
        >
            {children}
        </button>
    );
};

interface CarouselIndicatorGroupProps extends Omit<ComponentPropsWithRef<"div">, "children"> {
    children: ReactNode | ((props: { index: number }) => ReactNode);
    className?: string;
}

const CarouselIndicatorGroup = ({ children, className, ...props }: CarouselIndicatorGroupProps) => {
    const { scrollSnaps } = useCarousel();
    if (typeof children === "function") {
        return (
            <div className={cx("flex gap-2", className)} {...props}>
                {scrollSnaps.map((_, index) => (
                    <span key={index}>{children({ index })}</span>
                ))}
            </div>
        );
    }
    return (
        <div className={className} {...props}>
            {children}
        </div>
    );
};

export const Carousel = {
    Root: CarouselRoot,
    Content: CarouselContent,
    Item: CarouselItem,
    PrevTrigger: CarouselPrevTrigger,
    NextTrigger: CarouselNextTrigger,
    IndicatorGroup: CarouselIndicatorGroup,
    Indicator: CarouselIndicator,
};
